import { doFormatCostBeforeSandToBack } from '@entities/common/costs/index.ts';
import { programsApi, useProgram, usePrograms } from '@entities/programs';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { xhr } from '@shared/config';
import { doCheckArray } from '@shared/helpers/index.ts';
import { useAppDispatch } from '@shared/hooks';
import { usePagesRedirect } from '@shared/hooks/use-pages-redirect.ts';
import type { EntityEditorT } from '@shared/types/common';
import { type Program } from '@shared/types/programs';
import { useUpdateProgramMutation } from '@src/api/api-hooks/programs/useUpdateProgramMutation.ts';
import { queryClient } from '@src/api/query-client.ts';
import { base64ToFile } from '@src/library/helpers/events/base64ToFile';
import { openNotifyModal, showPopup } from '@src/library/redux/modal';
import { type ProgramEditorValues } from '../lib';

export const useFormSubmit = ({
  programId,
  onSuccess,
  code,
}: {
  programId: number;
  code: string;
  onSuccess?: () => void;
}) => {
  const dispatch = useAppDispatch();

  const { setProgram } = useProgram(programId);
  const { addProgram } = usePrograms();

  const [progress, setProgress] = React.useState<number>(0);
  const [uploadStatus, setUploadStatus] = React.useState<'idle' | 'loading'>('idle');

  const { mutateAsync: updateProgramMutation } = useUpdateProgramMutation();
  const navigate = useNavigate();
  const { doRedirectOnPrevPage } = usePagesRedirect('programEditor');
  const config = {
    onUploadProgress: (progressEvent: any) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      setProgress(percentCompleted);
    },
  };

  const createFormData = (
    data: ProgramEditorValues & { costsEditor?: EntityEditorT<{ cost: string }>[] }
  ) => {
    // console.log("data: ",data);
    const program = new FormData();
    program.append('title', data.title);

    program.append('with_likes', 'true');
    program.append('with_comments', `${data.with_comments}`);

    data?.description && program.append('description', data?.description);

    program.append('cost_base', data?.cost_base ? (data.cost_base * 100).toString() : '0');

    const costsArray = data.costsEditor?.map((groupCost) => ({
      cost: doFormatCostBeforeSandToBack(groupCost.cost),
      group: groupCost.entityId,
    }));
    // console.log(costsArray);

    program.append('costs', JSON.stringify(doCheckArray(costsArray) ? costsArray : []));

    // console.log(
    //   'JSON.stringify(doCheckArray(costsArray) ? costsArray : []): ',
    //   JSON.stringify(doCheckArray(costsArray) ? costsArray : [])
    // );

    program.append('access_duration', data.access_duration.toString());
    program.append('periodicity', JSON.stringify(data.periodicity));
    program.append('module', String(data.module));

    program.append('tags', JSON.stringify(data.tags));
    program.append('partitions', JSON.stringify(data.partitions));

    if (data.promo_image instanceof File) {
      program.append('promo_image', data.promo_image);
    } else if (typeof data.promo_image === 'string' && data.promo_image.includes('base64')) {
      program.append('promo_image', base64ToFile(data.promo_image));
    }

    program.append(
      'promo_video',
      !data.promo_video || data.promo_video === 0 ? '' : String(data.promo_video)
    );

    if (data.periodicity) {
      program.append('excluded_videos_period', JSON.stringify(data.excluded_videos_period));
    }
    data.exercises.map((exercise) => program.append('videos', JSON.stringify(exercise)));

    program.append('schedule', String(data.schedule));
    program.append('schedules', JSON.stringify(data.schedules));

    return program;
  };

  const createProgram = async (data: ProgramEditorValues) => {
    setUploadStatus('loading');
    const program = createFormData(data);
    const response = await xhr.post<Program>(programsApi, program, config);
    if (response.data) {
      // очищаем кеш, чтобы запросить программы заново (после создания новой)
      queryClient.setQueriesData({ queryKey: ['programsHistory'] }, undefined);
      queryClient.setQueriesData({ queryKey: ['programs'] }, undefined);
    }
    return response;
  };

  const updateProgram = async (data: ProgramEditorValues) => {
    const program = createFormData(data);
    // console.log('program:', program);
    // console.log("programForBack:",)
    // const response = await xhr.patch<Program>(`${programsApi}${programId}/`, program, config);
    const response = await updateProgramMutation({ id: programId, programData: program });
    return response;
  };
  const onSubmit = async (data: ProgramEditorValues) => {
    const hasNoVideos = !data.exercises.length;
    const isCreating = isNaN(programId);
    if (hasNoVideos) {
      dispatch(showPopup({ type: 'error', text: 'Добавьте хотя бы одно видео' }));
      document
        .getElementById('formVideos')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      let response;
      if (isCreating) {
        response = await createProgram(data);
        if (!response.data) {
          throw response;
        }
        addProgram(response.data);
      } else {
        response = await updateProgram(data);
        if (!response.data) {
          throw response;
        }
        setProgram(response.data);
      }

      const id = response.data.pk;
      // navigate(`/programs/${code}/program/${id}`);

      // dispatch(
      //   openDialogModal({
      //     title: 'Публикация',
      //     text: 'Опубликовать сейчас или сохранить без публикации?',
      //     confirmText: 'Сохранить',
      //     declineText: 'Опубликовать',
      //     confirm: () => {
      //       dispatch(
      //         showPopup({
      //           type: 'success',
      //           text: `Видеопрограмма успешно ${isNaN(programId) ? 'создана' : 'обновлена'}`,
      //         })
      //       );
      //       navigate(`/programs/${code}/program/${id}`);
      //     },
      //     decline: () => {
      //       const id = programId ? programId : response.data.pk;
      //     },
      //   })
      // );
      if (isCreating) {
        dispatch(
          showPopup({
            type: 'success',
            text: 'Видеопрограмма успешно создана',
          })
        );
      } else {
        dispatch(
          openNotifyModal({
            title: 'Уведомление',
            text: 'Изменения успешно внесены в программу',
            confirmText: 'Ок',
          })
        );
      }
      isCreating
        ? navigate(`/programs/${code}/program/${id}`, {
            state: { isRedirectAfterCreated: true },
          })
        : doRedirectOnPrevPage();

      // navigate(isCreating ? `/programs/${code}/program/${id}` : -1, {
      //   state: { isRedirectAfterCreated: isCreating },
      // });
      setUploadStatus('idle');
    } catch (error) {
      dispatch(
        showPopup({ type: 'error', text: 'Возникла проблема при загрузке видео на сервер' })
      );
      console.error(error);
      setUploadStatus('idle');
    }
  };

  const onProgramSubmit = async (data: ProgramEditorValues) => {
    // console.log("FormData:", data);
    await onSubmit(data);
    onSuccess && onSuccess();
  };

  return {
    createProgram,
    updateProgram,
    uploadStatus,
    onProgramSubmit,
    progress,
  };
};
